// Story 5.2 — TanStack Query read hook for the floor-price autopop.
//
// GET /api/v1/marketplace/floor/share?creator_user_id={}&tier={}
//
// Consumed by ListForSaleDialog on dialog open. `enabled` is meant to
// be wired to dialog-open state so the request only fires when the
// dialog actually mounts, not on every share-detail load. 5-minute
// staleTime caches subsequent opens within a session.
//
// Returns `{ floor_price_cents: number | null }`. `null` means "no
// active listings exist for this (creator, tier) bucket yet" — the
// dialog should leave the price input empty and let the user pick.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ShareTier = "common" | "preferred" | "premium" | "founders";

export interface ShareTierFloorPriceResponse {
  floor_price_cents: number | null;
}

export interface UseShareTierFloorPriceArgs {
  creatorUserId: string;
  tier: ShareTier;
  /** Whether to bucket by signed certs — the floor of a signed cert is not
   *  comparable to an unsigned one. */
  signed: boolean;
  enabled: boolean;
}

async function fetchShareTierFloorPrice(
  creatorUserId: string,
  tier: ShareTier,
  signed: boolean,
): Promise<ShareTierFloorPriceResponse> {
  const url = `/api/v1/marketplace/floor/share?creator_user_id=${encodeURIComponent(
    creatorUserId,
  )}&tier=${encodeURIComponent(tier)}&signed=${signed ? "true" : "false"}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Floor-price lookup failed (HTTP ${response.status})`);
  }
  const body = (await response.json()) as { data: ShareTierFloorPriceResponse };
  return body.data;
}

export function useShareTierFloorPrice(
  args: UseShareTierFloorPriceArgs,
): UseQueryResult<ShareTierFloorPriceResponse, Error> {
  return useQuery<ShareTierFloorPriceResponse, Error>({
    queryKey: [
      "marketplace-floor-share",
      args.creatorUserId,
      args.tier,
      args.signed,
    ],
    queryFn: () =>
      fetchShareTierFloorPrice(args.creatorUserId, args.tier, args.signed),
    enabled: args.enabled,
    staleTime: 5 * 60 * 1000,
  });
}
